import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Archive, Trash2 } from 'lucide-react'
import {
  listWalls, createWall, deleteWall, listDeletionCertificates, deletionCertificateUrl,
  type EthicalWall,
} from '../api'
import { useAuth } from '../auth'
import { traceWallCreate } from '../tracking/searchTrace'

// createWall + emit an end-user "wall creation" trace on success (Studio →
// Searches, Privacy page). Best-effort telemetry; no-op for staff. Ken 2026-08-23.
function createWallTracked(p: Parameters<typeof createWall>[0]) {
  return createWall(p).then((r) => {
    traceWallCreate({ match: p.match, mode: p.mode, source: 'privacy' })
    return r
  })
}

// Privacy page (heading "Walls"). Left rail: a collapsible Privacy Guide —
// in-app documentation so users don't have to remember the methods (Heather
// 2026-08-19), ordered weakest→strongest: Archive · Delete (draft) · Walls.
// Right: the wall controls, tiered owner / own-mail.

const inputCls = 'rounded border me-border bg-transparent px-2 py-1 text-[13px] me-text'

/* ─── Privacy Guide (left rail) ──────────────────────────────────────── */

function StrengthBars({ level }: { level: 1 | 2 | 3 }) {
  return (
    <span className="inline-flex items-end gap-0.5" title={`Strength ${level} of 3`} aria-label={`strength ${level} of 3`}>
      {[1, 2, 3].map((i) => (
        <span key={i} className="w-1 rounded-sm"
              style={{ height: 3 + i * 3, background: i <= level ? 'var(--me-fresh, #3f9)' : 'var(--me-border, #d6d3cd)' }} />
      ))}
    </span>
  )
}

function GuideItem({ title, level, summary, defaultOpen, children }: {
  title: string; level: 1 | 2 | 3; summary: string; defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div className="border-t me-border py-2 first:border-t-0">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-left">
        <ChevronRight size={13} className={`shrink-0 me-text-mute transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="flex-1 text-[13px] font-medium me-text">{title}</span>
        <StrengthBars level={level} />
      </button>
      {!open && <div className="ml-5 mt-0.5 text-[11px] me-text-mute">{summary}</div>}
      {open && <div className="ml-5 mt-2 space-y-2 text-[12px] me-text-mute leading-relaxed">{children}</div>}
    </div>
  )
}

// Small inline "snapshots" built from the app's own tokens — attractive and
// always in sync. Real screenshots can drop in later where wanted.
function AidBox({ children }: { children: React.ReactNode }) {
  return <div className="mt-1 rounded border me-border p-2" style={{ background: 'var(--me-selected)' }}>{children}</div>
}

function PrivacyGuide() {
  // The whole guide collapses (persisted) — the per-method collapse below is
  // unchanged (Ken 2026-08-19).
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('privacyGuideOpen') !== '0' } catch { return true }
  })
  const toggle = () => {
    const n = !open
    setOpen(n)
    try { localStorage.setItem('privacyGuideOpen', n ? '1' : '0') } catch { /* ignore */ }
  }
  return (
    <div className="rounded border me-border p-4">
      <button onClick={toggle} className="flex w-full items-center gap-2 text-left">
        <ChevronRight size={13} className={`shrink-0 me-text-mute transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="flex-1 text-[13px] font-semibold me-text">Privacy guide</span>
      </button>

      {open && (
        <div className="mt-1">
          <p className="mb-1 text-[11px] me-text-mute">Three ways to control who sees what.</p>

          <GuideItem title="Archive" level={1} summary="Hide a matter in the sidebar.">
            <p>When you hover over any matter in the sidebar, an icon of a filing box pops up. Click
              that icon to move the matter and all its email and document content into
              <span className="me-text"> Archived</span>, a greyed-out area below the folder tree.
              This hides but does not delete matters, and is reversible.</p>
            <AidBox>
              <div className="flex items-center gap-2">
                <span className="flex-1 truncate text-[11px] me-text">M-412 MSAT Program Policy</span>
                <Archive size={12} className="me-text-mute" />
              </div>
            </AidBox>
          </GuideItem>

          <GuideItem title="Delete" level={3} summary="Permanently delete an archived matter.">
            <p>Delete from Matterlink any matter (and its contents) in the
              <span className="me-text"> Archived</span> list.</p>
            <p>A certificate of deletion is provided, on this page, after you purge a matter with
              Delete. The original email or documents are, as always, still in your Gmail account.</p>
            <AidBox>
              <div className="flex items-center gap-1.5 text-[11px] me-text-mute">
                <Trash2 size={12} /> Archive → Purge permanently
              </div>
            </AidBox>
          </GuideItem>

          <GuideItem title="Walls" level={3}
                     summary="Reactive and proactive ways to make email private.">
            <p>‘Walls’ provide reactive and proactive methods for making incoming and outgoing
              email private.</p>
            <p>The proactive methods are self-explanatory and configurable with the tools to the
              right. They may be turned on or off, on this page, at any time.</p>
            <p>Attorney-users can also use the <span className="me-text">Wall</span> button in the
              upper right-hand corner of an email’s summary pane to hide that email from other
              Matterlink users instantly.</p>
            <AidBox>
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center rounded border me-border px-1.5 py-0.5 text-[10px] me-text">Refile</span>
                <span className="inline-flex items-center rounded border me-border px-1.5 py-0.5 text-[10px] me-text">Wall</span>
              </div>
            </AidBox>
          </GuideItem>
        </div>
      )}
    </div>
  )
}

/* ─── Wall creation form + row ───────────────────────────────────────── */

function WallForm({ title, note, hasSubject, submitLabel, onCreate }: {
  title: string
  note?: string
  hasSubject?: boolean
  submitLabel: string
  onCreate: (sender: string, subject: string) => Promise<{ walled_count: number }>
}) {
  const qc = useQueryClient()
  const [sender, setSender] = useState('')
  const [subject, setSubject] = useState('')
  const [flash, setFlash] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const ok = sender.trim().includes('@') && (!hasSubject || subject.trim().length > 0) && !busy

  const submit = async () => {
    setBusy(true)
    try {
      const r = await onCreate(sender.trim(), subject.trim())
      setFlash(`Walled ${r.walled_count} existing item${r.walled_count === 1 ? '' : 's'}. Future ones are caught on arrival.`)
      setSender(''); setSubject(''); qc.invalidateQueries()
      setTimeout(() => setFlash(null), 5000)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setFlash(detail || 'Could not create the wall.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-4 rounded border me-border p-4">
      <div className="mb-1 text-[13px] font-medium me-text">{title}</div>
      {note && <p className="mb-2 text-[12px] me-text-mute leading-relaxed">{note}</p>}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input value={sender} onChange={(e) => setSender(e.target.value)}
                 placeholder="sender@example.com" className={`${inputCls} flex-1`} />
          {hasSubject && (
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
                   placeholder="exact subject line" className={`${inputCls} flex-1`} />
          )}
        </div>
        <div>
          <button disabled={!ok} onClick={submit}
                  className="me-chrome me-btn me-btn-outline disabled:opacity-50">
            {busy ? 'Walling…' : submitLabel}
          </button>
        </div>
      </div>
      {flash && <div className="mt-2 text-[12px] me-text">{flash}</div>}
    </div>
  )
}

function WallRow({ w, canRemove }: { w: EthicalWall; canRemove: boolean }) {
  const qc = useQueryClient()
  const del = useMutation({ mutationFn: () => deleteWall(w.id), onSuccess: () => qc.invalidateQueries() })
  const mode = w.hide_mode === 'hidden' ? 'From Matterlink' : 'From other users'
  return (
    <div className="flex items-start justify-between gap-3 rounded border me-border p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[12px]">
          <span className="rounded-sm border me-border px-1.5 py-0.5 text-[10px] uppercase me-text-mute">{mode}</span>
          <span className="truncate me-text font-medium" title={w.label}>{w.label}</span>
        </div>
        <div className="mt-1 text-[11px] me-text-mute">
          Hides {w.covered_count} item{w.covered_count === 1 ? '' : 's'}
          {w.recipient_email ? ` · to ${w.recipient_email}` : ''}
          {w.created_by ? ` · by ${w.created_by}` : ''}
        </div>
      </div>
      {canRemove && (
        <button
          onClick={() => {
            if (window.confirm('Remove this wall? The hidden emails become visible again.'))
              del.mutate()
          }}
          className="me-chrome me-btn me-btn-outline shrink-0" title="Un-wall">
          Un-wall
        </button>
      )}
    </div>
  )
}

/* ─── Deletion record (level-b certificates) ────────────────────────── */

function DeletionRecord() {
  const certsQ = useQuery({ queryKey: ['deletion-certs'], queryFn: listDeletionCertificates })
  const certs = certsQ.data || []
  return (
    <div className="mt-10">
      <div className="mb-2 text-[13px] font-medium me-text">Deletion record ({certs.length})</div>
      {certsQ.isLoading && <div className="text-[12px] me-text-mute">Loading…</div>}
      {!certsQ.isLoading && certs.length === 0 && (
        <div className="text-[12px] me-text-mute">No permanent deletions yet. Purging a matter records a certificate here.</div>
      )}
      <div className="flex flex-col gap-2">
        {certs.map((c) => (
          <div key={c.id} className="flex items-start justify-between gap-3 rounded border me-border p-3">
            <div className="min-w-0">
              <div className="truncate text-[12px] font-medium me-text">M-{c.matter_seq} — {c.matter_name}</div>
              <div className="mt-1 text-[11px] me-text-mute">
                Purged {c.item_count} item{c.item_count === 1 ? '' : 's'}
                {c.purged_by ? ` · by ${c.purged_by}` : ''} · {new Date(c.purged_at).toLocaleString()}
              </div>
            </div>
            <a href={deletionCertificateUrl(c.id)} download
               className="me-chrome me-btn me-btn-outline shrink-0" title="Download certificate of deletion">
              Certificate
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function Privacy() {
  const auth = useAuth()
  const canManage = auth.status === 'authenticated' && !!auth.user.can_manage_walls
  const canOwn = auth.status === 'authenticated' && !!auth.user.can_wall_own_mail
  const myId = auth.status === 'authenticated' ? auth.user.id : null

  const wallsQ = useQuery({ queryKey: ['walls'], queryFn: listWalls, enabled: canManage || canOwn })

  if (!canManage && !canOwn) {
    return <div className="p-8 me-text-mute">You don’t have access to privacy settings.</div>
  }

  const walls = wallsQ.data || []
  const mine = walls.filter((w) => w.created_by_id === myId)
  const others = walls.filter((w) => w.created_by_id !== myId)

  return (
    <div className="me-scroll h-full overflow-y-auto p-6">
      {/* Walls stays centered (as before the guide existed); the guide floats
          into the left margin on wide screens, and stacks above on narrow so it
          never pushes Walls off-center (Ken 2026-08-19). */}
      <div className="relative mx-auto max-w-3xl">
        <aside className="mb-6 2xl:mb-0 2xl:absolute 2xl:right-full 2xl:top-0 2xl:mr-8 2xl:w-64">
          <PrivacyGuide />
        </aside>

        <main>
          <h1 className="mb-6 text-lg font-semibold me-text">Walls</h1>

        {canManage ? (
          <>
            <WallForm
              title="Wall a sender from Matterlink"
              note="Hides all mail from this sender from everyone in Matterlink — including you. The rows stay in the system but no one sees them."
              submitLabel="Wall from Matterlink"
              onCreate={(s) => createWallTracked({ match: 'sender', mode: 'hidden', senderEmail: s })}
            />
            <WallForm
              title="Wall a sender from other Matterlink users"
              note="Hides this sender's mail from other users; you keep access."
              submitLabel="Wall from other users"
              onCreate={(s) => createWallTracked({ match: 'sender', mode: 'restricted', senderEmail: s })}
            />
            <WallForm
              title="Wall a sender and subject line from other Matterlink users"
              hasSubject
              note="Hides only mail from this sender whose subject line is exactly this, from other users. A codeword arranged with the sender ahead of time."
              submitLabel="Wall sender + subject"
              onCreate={(s, subj) => createWallTracked({ match: 'sender_subject', mode: 'restricted', senderEmail: s, subject: subj })}
            />
          </>
        ) : (
          <WallForm
            title="Wall a sender from Matterlink"
            note="Hides all mail sent to you from this sender — it disappears from Matterlink for everyone, including you. (You’ll still have it in your own email.)"
            submitLabel="Wall from Matterlink"
            onCreate={(s) => createWallTracked({ match: 'sender', mode: 'hidden', senderEmail: s })}
          />
        )}

        <div className="mb-2 mt-6 text-[13px] font-medium me-text">Your active walls ({mine.length})</div>
        {wallsQ.isLoading && <div className="text-[12px] me-text-mute">Loading…</div>}
        {!wallsQ.isLoading && mine.length === 0 && (
          <div className="text-[12px] me-text-mute">No walls yet. Add one above{canManage ? ', or use the “Wall” button on any email.' : '.'}</div>
        )}
        <div className="flex flex-col gap-2">
          {mine.map((w) => <WallRow key={w.id} w={w} canRemove />)}
        </div>

        {canManage && (
          <>
            <div className="mb-2 mt-8 text-[13px] font-medium me-text">Other users’ active walls ({others.length})</div>
            {others.length === 0 && (
              <div className="text-[12px] me-text-mute">None yet. Walls other users create appear here (read-only).</div>
            )}
            <div className="flex flex-col gap-2">
              {others.map((w) => <WallRow key={w.id} w={w} canRemove={false} />)}
            </div>
          </>
        )}

        <DeletionRecord />
        </main>
      </div>
    </div>
  )
}
